// SaveAsPlaybookDialog — turn the current chat shell conversation into
// a saved playbook + tag it against the 100-skill AI Mastery Path.
//
// Why this lives next to UnifiedChatShell:
//   The shell holds the only authoritative copy of the live transcript.
//   The dialog accepts that snapshot as a prop instead of re-fetching it
//   so a user can save mid-conversation without dropping any in-flight
//   streaming state.
//
// What it does on submit:
//   1. Persists the transcript into `chat_conversations` (Supabase) so
//      it surfaces in the user's saved-chats history. Anonymous users
//      get a friendly "sign in to save" toast — we don't write a row.
//   2. For each tagged skill, fires `recordSkillLaunch(label)` which
//      bumps the local mastery journey ledger so /roadmap shows ✓ on
//      the matching squares immediately. (Phase 5 will replace this
//      local ledger with a server-side `user_mastery_skills` table.)
//
// Skill suggestion:
//   We hit the existing `tutor-chat` edge function with a tight system
//   prompt asking for 1–3 skill numbers. It's a guest-friendly endpoint
//   so the call works even before sign-up — but we only use the result
//   to pre-select pills; the user is the final arbiter.
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, BookmarkPlus, Check, Image as ImageIcon, RefreshCw, X as XIcon, Lock, Link2, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMasteryJourney } from "@/hooks/useMasteryJourney";
import {
  AI_MASTERY_PATH,
  MASTERY_TIERS,
  type MasteryTier,
  type MasterySkill,
} from "@/data/aiMasteryPath";

/**
 * Tag-limit constants. The product rule is "exactly 1–3 mastery skills
 * per saved playbook" — any more dilutes the signal on /mastery, any
 * less means the playbook contributes nothing measurable. Centralised
 * here so the picker, helper text, and save guard stay in sync.
 */
const MIN_SKILLS = 1;
const MAX_SKILLS = 3;

/**
 * Visibility options for a saved playbook. Mirrors the
 * `chat_conversations.visibility` CHECK constraint:
 *   - private  → owner only (default; safest)
 *   - unlisted → anyone with the share link (uses share_token flow)
 *   - public   → discoverable, readable by anyone
 * Keep this in sync with the migration if values ever change.
 */
type Visibility = "private" | "unlisted" | "public";
const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: Visibility;
  label: string;
  hint: string;
  Icon: typeof Lock;
}> = [
  {
    value: "private",
    label: "Private",
    hint: "Only you can open it. Safe default.",
    Icon: Lock,
  },
  {
    value: "unlisted",
    label: "Unlisted",
    hint: "Anyone with the share link can read it.",
    Icon: Link2,
  },
  {
    value: "public",
    label: "Public",
    hint: "Discoverable. Anyone can read and learn from it.",
    Icon: Globe2,
  },
];

const TIER_ORDER: MasteryTier[] = [
  "Awareness",
  "Application",
  "Leverage",
  "Systems",
  "Architecture",
];

/** Minimal turn shape the dialog needs (matches UnifiedChatShell.Turn). */
export interface DialogTurn {
  role: "user" | "assistant";
  content: string;
  model?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Snapshot of the current transcript; safe to pass empty until open. */
  turns: DialogTurn[];
  /** Default title (e.g. the playbook title or first user prompt). */
  defaultTitle?: string;
  /** Optional model used for the bulk of the chat — stored as metadata. */
  primaryModel?: string;
  /**
   * When set, the dialog *updates* an existing chat_conversations row
   * instead of inserting a new one. Before the update happens we call
   * `snapshot_playbook_revision` so the prior state is preserved as a
   * version (kept 30 days). Pass undefined for a brand-new playbook.
   */
  existingConversationId?: string;
}

export default function SaveAsPlaybookDialog({
  open,
  onOpenChange,
  turns,
  defaultTitle = "",
  primaryModel = "",
  existingConversationId,
}: Props) {
  const { user } = useAuth();
  const { recordSkillLaunch } = useMasteryJourney();

  const [title, setTitle] = useState(defaultTitle);
  const [summary, setSummary] = useState("");
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // AI-generated cover + teaser. Both are optional — the user can save
  // without them — but they live together because we generate them in a
  // single edge-function call from the same conversation context.
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [teaser, setTeaser] = useState<string>("");
  const [coverLoading, setCoverLoading] = useState(false);
  // Visibility — defaults to private so a save never unintentionally
  // exposes a transcript. The user opts in to wider reach explicitly.
  const [visibility, setVisibility] = useState<Visibility>("private");
  // Edit summary for revisions. Only relevant when updating an existing
  // playbook — captured per-save so the version history can show *why*
  // each revision was made (e.g. "added cold-email examples").
  const [editSummary, setEditSummary] = useState<string>("");

  const isUpdate = Boolean(existingConversationId);

  // Reset whenever a new save session opens. Pulling state from props
  // would create a stale-default footgun if the user reopens with a
  // freshly-edited title.
  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle || firstUserLine(turns) || "Untitled playbook");
    setSummary(firstAssistantSnippet(turns));
    setPicked(new Set());
    // Reset cover/teaser too — they're tied to a specific save session.
    setCoverUrl(null);
    setTeaser("");
    setCoverLoading(false);
    setVisibility("private");
    setEditSummary("");
  }, [open, defaultTitle, turns]);

  /** Call playbook-cover to generate teaser + cover image from the chat. */
  const generateCover = async () => {
    if (coverLoading) return;
    if (turns.length === 0) {
      toast.info("Have a chat first — there's nothing to draw from yet.");
      return;
    }
    if (!user) {
      toast.info("Sign in to generate a cover image.");
      return;
    }
    setCoverLoading(true);
    try {
      const payload = {
        title: title.trim() || "Untitled playbook",
        // Send a slim shape — the function only needs role + content.
        turns: turns.map((t) => ({
          role: t.role,
          content: t.content.slice(0, 2000),
        })),
        model: primaryModel || undefined,
      };
      const { data, error } = await supabase.functions.invoke(
        "playbook-cover",
        { body: payload },
      );
      if (error) {
        // Surface the upstream message when we can — supabase wraps it as
        // a plain Error so we read the response from the context if any.
        const msg = (error as { message?: string })?.message || "Cover generation failed";
        throw new Error(msg);
      }
      const result = data as { teaser?: string; cover_image_url?: string; error?: string };
      if (result?.error) throw new Error(result.error);
      if (result?.cover_image_url) setCoverUrl(result.cover_image_url);
      if (result?.teaser) setTeaser(result.teaser);
      toast.success("Cover + teaser ready");
    } catch (e) {
      const err = e as Error;
      toast.error(err.message || "Couldn't generate a cover.");
    } finally {
      setCoverLoading(false);
    }
  };

  /** Auto-suggest skills the conversation likely demonstrates. */
  const suggestSkills = async () => {
    if (suggestLoading) return;
    if (turns.length === 0) {
      toast.info("Have a chat first — there's nothing to tag yet.");
      return;
    }
    setSuggestLoading(true);
    try {
      // Tutor-chat is a streaming endpoint, so we accumulate then parse.
      // We give it the 100-skill list inline (small enough; 5KB tops)
      // and ask for a JSON array of numbers in tightly bounded output.
      const skillList = AI_MASTERY_PATH.map(
        (s) => `${s.num}. [${s.tier}] ${s.label}`,
      ).join("\n");

      const transcriptPreview = turns
        .slice(-8)
        .map((t) => `${t.role.toUpperCase()}: ${t.content.slice(0, 600)}`)
        .join("\n\n")
        .slice(0, 6000);

      const prompt = `Pick 1-3 skills from the 100-skill AI Mastery Path that this chat best demonstrates.\n\nReply with ONLY a JSON array of skill numbers, nothing else. Example: [12, 47]\n\n--- SKILLS ---\n${skillList}\n\n--- CHAT ---\n${transcriptPreview}`;

      const FUNC_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tutor-chat`;
      const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
      const { data: { session } } = await supabase.auth.getSession();
      const bearer = session?.access_token || anon;

      const resp = await fetch(FUNC_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearer}`,
          apikey: anon,
        },
        body: JSON.stringify({
          messages: [{ role: "user", content: prompt }],
          context: { surface: "save-as-playbook" },
        }),
      });
      if (!resp.ok || !resp.body) throw new Error("suggestion failed");

      // Stream-collect, then extract JSON array.
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let textBuffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, nl);
          textBuffer = textBuffer.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as
              | string
              | undefined;
            if (content) acc += content;
          } catch {
            /* ignore partial */
          }
        }
      }

      const match = acc.match(/\[[\d,\s]+\]/);
      if (!match) {
        toast.info("AI couldn't pick clean tags — choose them yourself.");
        return;
      }
      const nums = JSON.parse(match[0]) as number[];
      const valid = nums.filter(
        (n) => Number.isInteger(n) && n >= 1 && n <= 100,
      );
      if (valid.length === 0) {
        toast.info("No matching skills detected.");
        return;
      }
      const clamped = valid.slice(0, MAX_SKILLS);
      setPicked(new Set(clamped));
      toast.success(
        `Suggested ${clamped.length} skill${clamped.length > 1 ? "s" : ""}`,
      );
    } catch (e) {
      console.warn("[suggest-skills]", e);
      toast.error("Couldn't auto-suggest. Pick skills manually.");
    } finally {
      setSuggestLoading(false);
    }
  };

  const togglePick = (num: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(num)) {
        next.delete(num);
      } else {
        // Hard-cap at MAX_SKILLS — show a toast on the *first* over-tap
        // so the user understands why nothing happened, but don't spam
        // it on repeat clicks.
        if (next.size >= MAX_SKILLS) {
          toast.warning(`Pick up to ${MAX_SKILLS} skills`, {
            description: "Untag one to swap it for another.",
            id: "mastery-tag-cap",
          });
          return prev;
        }
        next.add(num);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    if (!title.trim()) {
      toast.error("Give it a title first.");
      return;
    }
    if (turns.length === 0) {
      toast.error("Nothing to save yet — start a conversation.");
      return;
    }
    // Enforce 1–3 mastery tags. The Save button is disabled at the same
    // bounds, but we double-check here so AI-suggest / programmatic
    // changes can't bypass it.
    if (picked.size < MIN_SKILLS) {
      toast.error(`Tag at least ${MIN_SKILLS} mastery skill before saving.`);
      return;
    }
    if (picked.size > MAX_SKILLS) {
      toast.error(`Pick at most ${MAX_SKILLS} skills — untag a few first.`);
      return;
    }

    setSaving(true);
    try {
      // Mark skills first; this works even for guests (localStorage)
      // and gives instant feedback on /roadmap the moment they navigate.
      const tagged = AI_MASTERY_PATH.filter((s) => picked.has(s.num));
      for (const s of tagged) recordSkillLaunch(s.label);

      // Persist transcript only for signed-in users — schema requires
      // user_id NOT NULL. Guests still get the mastery credit above.
      if (user) {
        const messagesPayload = turns.map((t) => ({
          role: t.role,
          content: t.content,
          model: t.model ?? null,
        }));
        let conversationId: string | undefined;

        if (isUpdate && existingConversationId) {
          // === UPDATE PATH ===
          // 1) Snapshot the *current* DB state into a new revision row
          //    so the user can roll back to whatever existed before this
          //    save. We do this server-side via the SECURITY DEFINER
          //    function — RLS+ownership are enforced inside the fn.
          const { error: snapErr } = await supabase.rpc(
            "snapshot_playbook_revision",
            {
              _conversation_id: existingConversationId,
              _edit_summary: editSummary.trim().slice(0, 500),
            },
          );
          if (snapErr) {
            // Surface the snapshot error but DON'T overwrite — losing
            // history silently would defeat the point of versioning.
            throw snapErr;
          }

          // 2) Apply the new payload over the live row.
          const { error: updErr } = await supabase
            .from("chat_conversations")
            .update({
              title: title.trim().slice(0, 200),
              model: primaryModel,
              messages: messagesPayload,
              message_count: turns.length,
              cover_image_url: coverUrl || null,
              teaser: teaser.trim() ? teaser.trim().slice(0, 280) : null,
              visibility,
              is_shared: visibility !== "private",
              last_activity_at: new Date().toISOString(),
            })
            .eq("id", existingConversationId)
            .eq("user_id", user.id);
          if (updErr) throw updErr;
          conversationId = existingConversationId;

          // For updates we also reset the per-playbook skill credits to
          // match the new tag set. Old credit rows are deleted; the
          // historical tag set is preserved inside the revision snapshot.
          await supabase
            .from("playbook_skill_credits")
            .delete()
            .eq("conversation_id", existingConversationId)
            .eq("user_id", user.id);
        } else {
          // === INSERT PATH (brand new playbook) ===
          const { data: inserted, error } = await supabase
            .from("chat_conversations")
            .insert({
              user_id: user.id,
              title: title.trim().slice(0, 200),
              model: primaryModel,
              messages: messagesPayload,
              message_count: turns.length,
              is_bookmarked: true,
              cover_image_url: coverUrl || null,
              teaser: teaser.trim() ? teaser.trim().slice(0, 280) : null,
              visibility,
              is_shared: visibility !== "private",
            })
            .select("id")
            .single();
          if (error) throw error;
          conversationId = inserted?.id;
        }

        // Persist the per-playbook skill credits so /account can list
        // exactly which mastery skills came from each saved playbook
        // and when. Failure here is non-fatal — the playbook is already
        // saved; we just surface a soft warning.
        if (conversationId && tagged.length > 0) {
          const { error: creditErr } = await supabase
            .from("playbook_skill_credits")
            .insert(
              tagged.map((s) => ({
                user_id: user.id,
                conversation_id: conversationId!,
                skill_num: s.num,
              })),
            );
          if (creditErr) {
            console.warn("[playbook_skill_credits]", creditErr);
          }
        }
        toast.success(
          isUpdate
            ? `Updated · prior version saved to history (30d)`
            : tagged.length > 0
              ? `Saved · tagged ${tagged.length} mastery skill${tagged.length > 1 ? "s" : ""}`
              : "Saved to your playbooks",
        );
      } else {
        toast.success(
          tagged.length > 0
            ? `Tagged ${tagged.length} skill${tagged.length > 1 ? "s" : ""} · sign in to save the transcript`
            : "Sign in to save the transcript",
        );
      }
      onOpenChange(false);
    } catch (e) {
      const err = e as Error;
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Group all 100 skills by tier for the picker grid.
  const tierGroups = useMemo(
    () =>
      TIER_ORDER.map((tier) => ({
        tier,
        meta: MASTERY_TIERS[tier],
        skills: AI_MASTERY_PATH.filter((s) => s.tier === tier),
      })),
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2">
            <BookmarkPlus className="w-4 h-4 text-primary" />
            {isUpdate ? "Save new version" : "Save as playbook"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {isUpdate ? (
              <>
                Update this playbook. The current version is snapshotted
                first — you can roll back any time within{" "}
                <span className="text-primary">30 days</span> from the
                history panel.
              </>
            ) : (
              <>
                Bookmark this conversation and tag the AI Mastery skills
                it demonstrates. Tagged skills show up checked on{" "}
                <span className="text-primary">/roadmap</span>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 px-1">
          {/* Title + summary */}
          <div className="space-y-2">
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Cold-email rewrite playbook"
              maxLength={200}
              className="font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              Summary (optional)
            </label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="One line about what this playbook does…"
              maxLength={400}
              rows={2}
              className="font-mono text-xs resize-none"
            />
          </div>

          {/* Edit summary — only relevant when updating an existing
              playbook. It's stored on the *prior* revision row (the
              snapshot we take just before applying the update) so the
              history panel can show "@alex · added cold-email examples". */}
          {isUpdate && (
            <div className="space-y-2">
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                What changed?{" "}
                <span className="text-muted-foreground/60 normal-case">
                  (shown in history)
                </span>
              </label>
              <Input
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                placeholder="e.g. Added 2 follow-up examples"
                maxLength={500}
                className="font-mono text-xs"
              />
            </div>
          )}

          {/* Visibility — three-way segmented control. Wrapped in a
              single <fieldset> so screen readers announce it as a group;
              the active option is reflected via aria-pressed for AT users.
              Default is "private" — we want users to opt in to sharing,
              not opt out. */}
          <fieldset className="space-y-2">
            <legend className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              Visibility
            </legend>
            <div className="grid grid-cols-3 gap-1.5">
              {VISIBILITY_OPTIONS.map((opt) => {
                const active = visibility === opt.value;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setVisibility(opt.value)}
                    title={opt.hint}
                    className={`flex flex-col items-start gap-1 rounded-md border px-2 py-2 text-left transition-colors ${
                      active
                        ? "border-primary/60 bg-primary/10 text-primary"
                        : "border-border bg-card/30 text-muted-foreground hover:text-foreground hover:border-primary/30"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-[11px] font-mono font-medium">
                      <Icon className="w-3 h-3 shrink-0" />
                      {opt.label}
                    </span>
                    <span className="text-[10px] font-mono leading-tight text-muted-foreground/80">
                      {opt.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Cover + teaser — generated together from the live chat
              transcript via the playbook-cover edge function. We render
              the action button + result inline so the user can
              regenerate / clear without leaving the dialog. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                Cover &amp; teaser{" "}
                {(coverUrl || teaser) && (
                  <span className="text-primary">· generated</span>
                )}
              </label>
              <div className="flex items-center gap-1.5">
                {(coverUrl || teaser) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCoverUrl(null);
                      setTeaser("");
                    }}
                    disabled={coverLoading}
                    className="h-7 px-2 text-[10px] font-mono gap-1"
                    title="Clear generated cover and teaser"
                  >
                    <XIcon className="w-3 h-3" />
                    Clear
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateCover}
                  disabled={coverLoading || turns.length === 0 || !user}
                  title={
                    !user
                      ? "Sign in to generate a cover"
                      : turns.length === 0
                        ? "Have a chat first"
                        : coverUrl
                          ? "Regenerate cover & teaser"
                          : "Generate cover & teaser from this chat"
                  }
                  className="h-7 px-2 text-[10px] font-mono gap-1"
                >
                  {coverLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : coverUrl ? (
                    <RefreshCw className="w-3 h-3" />
                  ) : (
                    <ImageIcon className="w-3 h-3" />
                  )}
                  {coverLoading
                    ? "Generating…"
                    : coverUrl
                      ? "Regenerate"
                      : "Generate cover"}
                </Button>
              </div>
            </div>

            {coverLoading && (
              <div className="rounded-md border border-border bg-background/50 p-2 space-y-2">
                <div className="aspect-[16/9] w-full rounded-sm bg-secondary/60 animate-pulse" />
                <div className="h-3 w-3/4 rounded bg-secondary/60 animate-pulse" />
              </div>
            )}

            {!coverLoading && (coverUrl || teaser) && (
              <div className="rounded-md border border-border bg-background/50 p-2 space-y-2">
                {coverUrl && (
                  <img
                    src={coverUrl}
                    alt="Generated playbook cover"
                    loading="lazy"
                    className="w-full aspect-[16/9] object-cover rounded-sm border border-border/60"
                  />
                )}
                <Textarea
                  value={teaser}
                  onChange={(e) => setTeaser(e.target.value.slice(0, 280))}
                  placeholder="One-line teaser…"
                  maxLength={280}
                  rows={2}
                  className="font-mono text-xs resize-none"
                />
                <div className="flex items-center justify-end">
                  <span className="text-[10px] font-mono text-muted-foreground/60">
                    {teaser.length}/280
                  </span>
                </div>
              </div>
            )}

            {!user && (
              <p className="text-[10px] font-mono text-muted-foreground/80">
                Sign in to generate an AI cover image and teaser for this
                playbook.
              </p>
            )}
          </div>

          {/* Skill picker */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                Skills demonstrated{" "}
                <span
                  className={
                    picked.size === 0
                      ? "text-muted-foreground/60"
                      : picked.size > MAX_SKILLS
                        ? "text-destructive"
                        : "text-primary"
                  }
                >
                  · {picked.size}/{MAX_SKILLS} picked
                </span>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={suggestSkills}
                disabled={suggestLoading || turns.length === 0}
                className="h-7 px-2 text-[10px] font-mono gap-1"
              >
                {suggestLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3" />
                )}
                {suggestLoading ? "Thinking…" : "AI suggest"}
              </Button>
            </div>

            {/* Helper / warning strip. Three states:
                  - empty  → muted nudge to pick at least one
                  - at cap → primary-tinted "you're at the max" hint
                  - other  → counter that updates live
                We mount it above the grid so the user sees the rule
                *before* tapping pills. */}
            <div
              className={`text-[10px] font-mono px-2 py-1 rounded border ${
                picked.size === 0
                  ? "border-amber-500/30 bg-amber-500/5 text-amber-500"
                  : picked.size >= MAX_SKILLS
                    ? "border-primary/30 bg-primary/5 text-primary"
                    : "border-border bg-background/30 text-muted-foreground"
              }`}
              role="status"
              aria-live="polite"
            >
              {picked.size === 0
                ? `Pick ${MIN_SKILLS}–${MAX_SKILLS} skills this playbook demonstrates. At least 1 required to save.`
                : picked.size >= MAX_SKILLS
                  ? `Max ${MAX_SKILLS} tags reached — untag one to swap.`
                  : `${picked.size} of up to ${MAX_SKILLS} tags selected. Add ${MAX_SKILLS - picked.size} more or save now.`}
            </div>

            <div className="rounded-md border border-border p-2 space-y-3 bg-background/50">
              {tierGroups.map((g) => (
                <div key={g.tier} className="space-y-1">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${g.meta.pill}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${g.meta.dot}`} />
                      {g.tier}
                    </span>
                    <span className="text-muted-foreground/60">{g.meta.range}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {g.skills.map((s) => {
                      const on = picked.has(s.num);
                      // Disable un-selected pills once we hit the cap so
                      // the limit is *visible* (greyed-out) instead of
                      // only enforced on click. Selected pills stay
                      // active so the user can always untag.
                      const atCap = !on && picked.size >= MAX_SKILLS;
                      return (
                        <button
                          key={s.num}
                          type="button"
                          onClick={() => togglePick(s.num)}
                          disabled={atCap}
                          title={
                            atCap
                              ? `Max ${MAX_SKILLS} tags reached — untag one first`
                              : `#${s.num} · ${s.label}`
                          }
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono border transition-colors max-w-[200px] truncate ${
                            on
                              ? "bg-primary/15 border-primary/50 text-primary"
                              : atCap
                                ? "border-border/50 bg-card/20 text-muted-foreground/40 cursor-not-allowed"
                                : "border-border bg-card/30 text-muted-foreground hover:text-foreground hover:border-primary/30"
                          }`}
                        >
                          {on && <Check className="w-2.5 h-2.5 shrink-0" />}
                          <span className="truncate">
                            #{s.num} {s.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="font-mono text-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              saving ||
              !title.trim() ||
              picked.size < MIN_SKILLS ||
              picked.size > MAX_SKILLS
            }
            title={
              picked.size < MIN_SKILLS
                ? `Tag at least ${MIN_SKILLS} mastery skill before saving`
                : picked.size > MAX_SKILLS
                  ? `Pick at most ${MAX_SKILLS} mastery skills`
                  : "Save this playbook"
            }
            className="font-mono text-xs gap-1.5"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <BookmarkPlus className="w-3.5 h-3.5" />
            )}
            {isUpdate ? "Save new version" : "Save playbook"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Helpers ---------------------------------------------------------

function firstUserLine(turns: DialogTurn[]): string {
  const u = turns.find((t) => t.role === "user");
  return u ? u.content.slice(0, 80) : "";
}

function firstAssistantSnippet(turns: DialogTurn[]): string {
  const a = turns.find((t) => t.role === "assistant" && t.content.trim());
  if (!a) return "";
  return a.content.replace(/\s+/g, " ").trim().slice(0, 200);
}